import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, catchError, switchMap, throwError } from 'rxjs';
import { SwalService } from '../service/swal.service';
import { AuthService } from '../service/auth.service';

/**
 * Gestione errori centralizzata. Sostituisce il metodo redirect() e i blocchi
 * 401 ripetuti in ogni metodo di ConnectionService.
 *
 * - 401 (non autenticata): token assente/scaduto/invalido → rimuove il token,
 *   mostra il dialog e reindirizza al login. Ritorna EMPTY per "assorbire"
 *   l'errore (come prima faceva redirect()).
 * - 403 (autenticata ma senza permessi): NON fa logout → mostra "accesso negato"
 *   e riporta alla home, mantenendo la sessione. Ritorna EMPTY.
 * - Ogni altro errore: viene rilanciato così il chiamante può gestirlo.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const swal = inject(SwalService);
  const translate = inject(TranslateService);
  const platformId = inject(PLATFORM_ID);
  const auth = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        const isLogin = req.url.includes('/auth/login');
        const isRefresh = req.url.includes('/auth/refresh');

        // Login fallito (credenziali errate): lo gestisce il chiamante
        // (login_error mostra il messaggio inline), non è una sessione scaduta.
        if (isLogin) {
          return throwError(() => error);
        }

        // Il refresh stesso è fallito, o siamo lato server: la sessione è
        // davvero finita → pulizia + redirect. Rilancio l'errore così il
        // chiamante del refresh (AuthService) sblocca i richiedenti in attesa.
        if (isRefresh || !isPlatformBrowser(platformId)) {
          auth.clearSession();
          swal.errorDialog(
            translate.instant('swal.oops'),
            error.error?.message || translate.instant('swal.unauthorized'),
          );
          router.navigate(['/login']);
          return throwError(() => error);
        }

        // Access token scaduto su una richiesta normale: rinnova col cookie e
        // ripeti la richiesta originale col braccialetto nuovo. L'utente non
        // si accorge di nulla. Se il refresh fallisce, endSession() è già
        // scattato nel ramo isRefresh: qui assorbo per non duplicare il dialog.
        return auth.refreshAccessToken().pipe(
          switchMap((token) =>
            next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })),
          ),
          catchError(() => EMPTY),
        );
      }

      if (error.status === 403) {
        const code = error.error?.code;
        // Blocco da abbonamento: invece del generico "accesso negato" propongo
        // l'upgrade portando a Impostazioni → Piano. Resto sulla pagina corrente.
        if (
          code === 'SUBSCRIPTION_REQUIRED' ||
          code === 'PLAN_PRO_REQUIRED' ||
          code === 'PLAN_TEAM_REQUIRED'
        ) {
          const msg =
            error.error?.message || 'Passa a un piano superiore per usarla.';
          // Differito: il loadingInterceptor chiude il Swal nel finalize (subito
          // dopo questo catchError). Aprendo il dialog nel microtask successivo
          // evito che venga chiuso all'istante.
          setTimeout(() => {
            swal
              .confirm('Funzione non disponibile', msg, 'Vedi i piani', 'Chiudi')
              .then((res: any) => {
                if (res?.isConfirmed) router.navigate(['/abbonamenti']);
              });
          });
          // Rilancio l'errore (non EMPTY) così i chiamanti resettano il loro
          // stato di caricamento; il dialog generico va soppresso sul 403.
          return throwError(() => error);
        }

        swal.errorDialog(
          translate.instant('swal.oops'),
          error.error?.message || translate.instant('swal.forbidden'),
        );
        router.navigate(['/home']);
        return EMPTY;
      }

      return throwError(() => error);
    }),
  );
};
